import {
  isCoachHealthEvidenceSourceType,
  type CoachHealthEvidenceSourceType,
} from "@/lib/coachHealth/coachHealthEvidenceSourceRegistry";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const EVIDENCE_MARKER = "coachHealthEvidence";
const EVIDENCE_MARKER_VALUE = "1";
const EVIDENCE_IDENTITY_KEYS = [
  "sourceType",
  "sourceId",
  "relationshipRevision",
  "routeBindingId",
] as const;

export function stripCoachHealthEvidenceIntentParams(
  params: URLSearchParams,
): URLSearchParams {
  const stripped = new URLSearchParams(params);
  stripped.delete(EVIDENCE_MARKER);
  EVIDENCE_IDENTITY_KEYS.forEach((key) => stripped.delete(key));
  return stripped;
}

export type CoachHealthRoutedEvidenceSourceType = Exclude<
  CoachHealthEvidenceSourceType,
  "coach_philosophy"
>;

export interface CoachHealthEvidenceRouteIdentity {
  readonly sourceType: CoachHealthRoutedEvidenceSourceType;
  readonly sourceId: string;
  readonly leaderId: string;
  readonly relationshipRevision: string;
  readonly routeBindingId: string;
}

export type CoachHealthEvidenceRouteIntent =
  | { readonly kind: "ordinary" }
  | { readonly kind: "invalid" }
  | {
      readonly kind: "evidence";
      readonly identity: CoachHealthEvidenceRouteIdentity;
    };

function isUuid(value: string | null): value is string {
  return value !== null && UUID_PATTERN.test(value);
}

function evidenceRoutePathname(
  pathname: string,
  identity: CoachHealthEvidenceRouteIdentity,
): { pathname: string; baseParams: readonly (readonly [string, string])[] } {
  if (
    !pathname.startsWith("/") ||
    pathname.startsWith("//") ||
    pathname.includes("\\") ||
    pathname.includes("%") ||
    pathname.includes("?") ||
    pathname.includes("#")
  ) {
    throw new Error("Invalid evidence destination");
  }

  switch (identity.sourceType) {
    case "completed_coach_call":
      if (
        identity.routeBindingId !== identity.sourceId ||
        pathname !== `/coach/meetings/${identity.sourceId}`
      ) {
        throw new Error("Invalid completed-call destination");
      }
      return { pathname, baseParams: [] };
    case "coach_transcript_session_record":
      if (pathname !== `/coach/post-meeting/${identity.routeBindingId}`) {
        throw new Error("Invalid transcript-session destination");
      }
      return { pathname, baseParams: [] };
    case "coach_first_take":
      if (pathname !== `/coach/meetings/${identity.routeBindingId}/reflect`) {
        throw new Error("Invalid first-take destination");
      }
      return { pathname, baseParams: [] };
    case "coach_reviewable_transcript":
      if (
        identity.routeBindingId !== identity.sourceId ||
        pathname !== "/coach/reviews"
      ) {
        throw new Error("Invalid transcript-review destination");
      }
      return {
        pathname,
        baseParams: [["transcriptId", identity.routeBindingId]],
      };
    case "coaching_continuity_moment": {
      const reflectPath = `/coach/meetings/${identity.routeBindingId}/reflect`;
      if (pathname === reflectPath) return { pathname, baseParams: [] };

      const meeting = pathname.match(/^\/coach\/meetings\/([^/]+)$/);
      if (!meeting || !isUuid(meeting[1])) {
        throw new Error("Invalid continuity destination");
      }
      return {
        pathname,
        baseParams: [
          ["view", "review"],
          ["transcriptId", identity.routeBindingId],
        ],
      };
    }
  }
}

export function buildCoachHealthEvidenceRoute(
  pathname: string,
  identity: CoachHealthEvidenceRouteIdentity,
): string {
  if (
    !isUuid(identity.sourceId) ||
    !isUuid(identity.leaderId) ||
    !isUuid(identity.relationshipRevision) ||
    !isUuid(identity.routeBindingId)
  ) {
    throw new Error("Invalid evidence identity");
  }

  const destination = evidenceRoutePathname(pathname, identity);
  const params = new URLSearchParams(destination.baseParams);
  params.set("from", "leaders");
  params.set(EVIDENCE_MARKER, EVIDENCE_MARKER_VALUE);
  params.set("sourceType", identity.sourceType);
  params.set("sourceId", identity.sourceId);
  params.set("leaderId", identity.leaderId);
  params.set("relationshipRevision", identity.relationshipRevision);
  params.set("routeBindingId", identity.routeBindingId);
  return `${destination.pathname}?${params.toString()}`;
}

export function readCoachHealthEvidenceRouteIntent(
  pathname: string,
  search: string,
): CoachHealthEvidenceRouteIntent {
  const params = new URLSearchParams(search);
  const hasEvidenceIntent =
    params.has(EVIDENCE_MARKER) ||
    EVIDENCE_IDENTITY_KEYS.some((key) => params.has(key));
  if (!hasEvidenceIntent) return { kind: "ordinary" };

  const required = [
    "from",
    EVIDENCE_MARKER,
    "sourceType",
    "sourceId",
    "leaderId",
    "relationshipRevision",
    "routeBindingId",
  ] as const;
  if (!required.every((key) => params.getAll(key).length === 1)) {
    return { kind: "invalid" };
  }

  const sourceType = params.get("sourceType");
  const sourceId = params.get("sourceId");
  const leaderId = params.get("leaderId");
  const relationshipRevision = params.get("relationshipRevision");
  const routeBindingId = params.get("routeBindingId");
  if (
    params.get("from") !== "leaders" ||
    params.get(EVIDENCE_MARKER) !== EVIDENCE_MARKER_VALUE ||
    !sourceType ||
    !isCoachHealthEvidenceSourceType(sourceType) ||
    sourceType === "coach_philosophy" ||
    !isUuid(sourceId) ||
    !isUuid(leaderId) ||
    !isUuid(relationshipRevision) ||
    !isUuid(routeBindingId)
  ) {
    return { kind: "invalid" };
  }

  const identity: CoachHealthEvidenceRouteIdentity = {
    sourceType,
    sourceId,
    leaderId,
    relationshipRevision,
    routeBindingId,
  };
  try {
    if (`${pathname}${search}` !== buildCoachHealthEvidenceRoute(pathname, identity)) {
      return { kind: "invalid" };
    }
  } catch {
    return { kind: "invalid" };
  }
  return { kind: "evidence", identity };
}

export function isCanonicalCoachHealthEvidenceRoute(
  route: string,
  identity: CoachHealthEvidenceRouteIdentity,
): boolean {
  try {
    if (
      !route.startsWith("/") ||
      route.startsWith("//") ||
      route.includes("\\") ||
      route.includes("%")
    ) {
      return false;
    }
    const url = new URL(route, "https://coach-veera.invalid");
    if (url.origin !== "https://coach-veera.invalid" || url.hash) return false;
    return route === buildCoachHealthEvidenceRoute(url.pathname, identity);
  } catch {
    return false;
  }
}
